import React, {
  useCallback, useEffect, useRef, useState,
} from 'lib/react';
import PropTypes from 'prop-types';
import { EMBEDDED_EXPLORER_INITIAL_VIEW, EMBEDDED_EXPLORER_US_PARCEL_LAYER_NAME } from './EmbeddedExplorer.constants';
import {
  loadEmbeddedExplorerApi, createEmbeddedExplorerMap, createEmbeddedExplorerLocationMarker, removeEmbeddedExplorerLocationMarkers,
} from './EmbeddedExplorer.util';
import {
  getMapZoomForPoint,
  getGeocodeViewKey,
  shouldApplyInitialMapView,
} from './mapView.utils';

/**
 * EmbeddedExplorer component for displaying EagleView's Embedded Explorer map
 */
function EmbeddedExplorer(props) {
  const {
    mapOptions,
    onMapLoad,
    onMapError,
    mapPoints,
    onUpdatePinLocation,
    pinDragEnabled,
  } = props;

  const mapInstance = useRef();
  const retryTimeout = useRef();
  const appliedGeocodeViewKey = useRef('');
  const hasInitializedView = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Add location markers to the map
   */
  const addLocationMarkers = useCallback(() => {
    if (!mapInstance?.current) return;

    removeEmbeddedExplorerLocationMarkers(mapInstance.current);
    if (mapPoints?.length > 0) {
      const [{ address: { latitude: lat, longitude: lon }, userMovedThePin, usedZipFallback }] = mapPoints;
      const geocodeViewKey = getGeocodeViewKey({ latitude: lat, longitude: lon });
      const zoom = getMapZoomForPoint({ usedZipFallback, userMovedThePin });

      if (shouldApplyInitialMapView({
        hasInitializedView: hasInitializedView.current,
        userMovedThePin,
        geocodeViewKey,
        appliedGeocodeViewKey: appliedGeocodeViewKey.current,
      })) {
        mapInstance?.current.setView({
          lonLat: { lat, lon },
          zoom,
        });
        appliedGeocodeViewKey.current = geocodeViewKey;
        hasInitializedView.current = true;
      }

      const features = mapPoints
        .map(({ address: { latitude, longitude } }) => createEmbeddedExplorerLocationMarker(
          { latitude, longitude },
          pinDragEnabled,
        ));

      mapInstance?.current.addFeatures({
        geoJson: features,
      });
    } else {
      appliedGeocodeViewKey.current = '';
      hasInitializedView.current = false;
      mapInstance?.current.setView(EMBEDDED_EXPLORER_INITIAL_VIEW);
    }
  }, [mapPoints]);

  /**
   * Initialize the Embedded Explorer map
   */
  const initMap = useCallback(() => {
    try {
      // #genai: use optional chaining instead of manual null checks (Sonar S6582); semantics preserved for undefined/null/falsy 'window.ev'
      if (!window.ev?.EmbeddedExplorer) {
        throw new Error('Embedded Explorer API not loaded');
      }

      mapInstance.current = createEmbeddedExplorerMap('embedded-explorer-map', mapOptions);

      const addParcelLayer = () => {
        mapInstance.current.updateLayers({
          filter: (layer) => {
            if (layer.name === EMBEDDED_EXPLORER_US_PARCEL_LAYER_NAME) {
              // eslint-disable-next-line no-param-reassign
              layer.visible = true;
            }
          },
        });
      };

      if (mapInstance.current.getLayers().length > 0) {
        addParcelLayer();
      } else {
        mapInstance.current.on('onLayersDataLoad', () => {
          addParcelLayer();
        });
      }

      // add parcel layer
      mapInstance.current.on('onLayersDataLoad', () => {
        addParcelLayer();
      });

      mapInstance.current.on('featureDragEnd', (feature) => {
        const { coordinates: [longitude, latitude] } = feature;
        onUpdatePinLocation(0, { latitude, longitude });
      });

      addLocationMarkers();

      setIsLoading(false);
      setError(null);

      if (onMapLoad) {
        onMapLoad(mapInstance?.current);
      }
    } catch (err) {
      setError(err.message);
      setIsLoading(false);

      if (onMapError) {
        onMapError(err);
      }

      // Retry initialization after 2 seconds
      if (retryTimeout?.current) {
        clearTimeout(retryTimeout?.current);
      }
      retryTimeout.current = setTimeout(() => {
        setIsLoading(true);
        setError(null);
        initMap();
      }, 2000);
    }
  }, [mapOptions, onMapLoad, onMapError]);

  /**
   * Load the Embedded Explorer API and initialize the map
   */
  useEffect(() => {
    loadEmbeddedExplorerApi()
      .then(() => {
        initMap();
      })
      .catch((err) => {
        setError('Failed to load map. Please check your connection and try again.');
        setIsLoading(false);

        if (onMapError) {
          // #genai Preserve the callback's original DOM-event payload after the loader wraps it.
          onMapError(err.cause ?? err);
        }
      });

    // Cleanup function
    return () => {
      if (retryTimeout?.current) {
        clearTimeout(retryTimeout?.current);
      }

      // Clean up map instance if needed
      if (mapInstance?.current && typeof mapInstance?.current.destroy === 'function') {
        mapInstance?.current.destroy();
      }
    };
  }, []);

  useEffect(() => {
    addLocationMarkers();
  }, [mapPoints]);

  const loadingStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    textAlign: 'center',
    color: '#666',
  };

  const errorStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    textAlign: 'center',
    color: '#d32f2f',
    padding: '20px',
  };

  return (
    <>
      <div
        id="embedded-explorer-map"
        style={{ width: '100%', height: '100%', position: 'relative' }}
      />

      {isLoading && (
        <div style={loadingStyle}>
          <div>Loading map...</div>
        </div>
      )}

      {error && (
        <div style={errorStyle}>
          <div>Error loading map</div>
          <div style={{ fontSize: '14px', marginTop: '8px' }}>{error}</div>
        </div>
      )}
    </>
  );
}

EmbeddedExplorer.defaultProps = {
  mapOptions: {},
  onMapLoad: null,
  onMapError: null,
  mapPoints: [],
  pinDragEnabled: false,
};

EmbeddedExplorer.propTypes = {
  /** Additional map configuration options */
  mapOptions: PropTypes.shape({
    zoom: PropTypes.number,
    center: PropTypes.shape({
      latitude: PropTypes.number,
      longitude: PropTypes.number,
    }),
  }),
  /** Callback function called when the map loads successfully */
  onMapLoad: PropTypes.func,
  /** Callback function called when there's an error loading the map */
  onMapError: PropTypes.func,
  /** Array of map points */
  mapPoints: PropTypes.arrayOf(PropTypes.shape({
    latitude: PropTypes.number,
    longitude: PropTypes.number,
  })),
  /** Callback function called when the pin location is updated */
  onUpdatePinLocation: PropTypes.func.isRequired,
  /** Whether the map pin is movable */
  pinDragEnabled: PropTypes.bool,
};

export default EmbeddedExplorer;
